import { inject } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { isPropertyDefined } from "@recursyve/nice-ts-utils";
import type { NiceAsyncTypeaheadSearchResult } from "@recursyve/nice-ui-kit.v2";
import { NiceAsyncTypeaheadProvider } from "@recursyve/nice-ui-kit.v2";
import type { Observable } from "rxjs";
import { lastValueFrom } from "rxjs";
import { map } from "rxjs/operators";

import { I18nUtils } from "../../../../../utils/i18n.utils";
import type { Occupations } from "../../models/enums/occupations.model";
import { OccupationsApi } from "./occupations.api";

export class OccupationsTypeaheadApi extends NiceAsyncTypeaheadProvider<Occupations> {
    private readonly api = inject(OccupationsApi);
    private readonly translateService = inject(TranslateService);

    public override readonly resource = "occupations";
    private readonly pageSize = 20;

    public override async getById(id: number): Promise<Occupations> {
        return lastValueFrom(this.api.getById(id));
    }

    public override search(searchQuery: string, page: number): Observable<NiceAsyncTypeaheadSearchResult<Occupations>> {
        return this.api.findAll({ page, pageSize: this.pageSize, searchQuery }).pipe(
            map(items => ({
                items,
                nextPage: items.length === this.pageSize ? page + 1 : null
            }))
        );
    }

    public override format(occupation: Occupations): string {
        if (!isPropertyDefined(occupation, "value")) {
            return "-";
        }

        if (typeof occupation.value === "string") {
            return occupation.value;
        }

        return occupation.value[this.getDefaultLanguage()] ?? "-";
    }

    private getDefaultLanguage(): string {
        return this.translateService.getDefaultLang() ?? I18nUtils.DEFAULT_LANGUAGE;
    }
}
